import { PlayIcon } from "@heroicons/react/24/solid";
import { Badge } from "@via/components/badge.tsx";
import { BadgeStatus } from "@via/utils/constants.tsx";
import { isFalsyOrEmpty, isObjectEmpty } from "@via/utils/helpers.tsx";
import { type UseFormReturn } from "react-hook-form";
import {
  AppStatus,
  ContainerStatus,
  Permissions,
  PermissionType,
  normalizeRunStatus,
  PipelineType,
  RunStatus,
  ServiceTokenType,
} from "common";
export { getRunUploadDir } from "common";
import { type JSX, type ReactNode } from "react";

interface InputDetails {
  defVal?: string | string[] | boolean;
  dropDown?: string;
  checkbox?: boolean;
  textbox?: boolean;
  showSett?: string;
  inDescOpt?: string;
  inLabelOpt?: string;
  title?: string;
  tool?: string;
  autoform?: Autoform[];
  optional?: boolean;
  editor?: Record<string, any>;
  hidden?: boolean;
  /** Integration token configuration - specifies which token types to fetch */
  integrationToken?: {
    type: ServiceTokenType[];
  };
}

interface Autoform {
  condition: string;
  value: string;
}

export interface InputNode {
  id: string;
  inputId: string;
  name: string;
  inputDetails: InputDetails;
  qualifier: string;
}

export interface ProcessAndModuleOptions {
  longVarName: string;
  processName: string;
  processOrgName: string;
  onlyModuleName: string;
  inputs: InputNode[];
  style: Record<string, any>[];
}

export interface InputParameter {
  file_type: string;
  id: number;
  displayName: string;
  operator: string;
  operatorContent: string;
  optional: string;
  parameterId: number;
  qualifier: string;
  regExp?: string;
  sname: string;
  test: string;
  inputParameters: any[];
  outputParameters: any[];
}

interface IBaseVar {
  varName: string;
  defaultVal: string | string[] | null;
}

export interface IPanelObject {
  schema: ISchema[];
  style: IStyle[];
  pipelineDefault: IPipelineDefault[];
}

interface IPipelineDefault extends IBaseVar {
  processName: string;
}

interface ISchema extends IBaseVar {
  type: string | null;
  desc: string | null;
  tool: string | null;
  label: string | null;
  opt: string[] | string[][] | null;
  title: string | null;
  autoform: string[][] | null;
  showsett: string | string[] | null;
  optional: boolean | null;
  file: boolean | null;
  singleFile: boolean | null;
  hidden: boolean | null;
  editor: string | null;
  runReport: string | null;
  validate: string | null;
}

interface IStyle {
  multiCol?: string[] | string[][] | null;
  arr?: string[] | string[][] | null;
  cond?: string[][] | null;
  spreadsheet?: string[] | string[][] | null;
  requiredOneOf?: string[][] | null;
}

export interface AutoFill {
  condition: Record<string, any>;
  genCondition?: Record<string, any>;
  statement: Record<string, any>;
  library?: Record<string, any>;
}

type Option = { label: string; value: string };

export const getMainPipeline = (
  obj: Record<string, any>
): Record<string, any> => {
  if (!obj) {
    return {};
  }
  for (const [key] of Object.entries(obj)) {
    if (key.includes("main_pipeline")) {
      return obj[key];
    }
  }
  return {};
};

const fixParentheses = (outputName: string): string => {
  if (outputName.match(/(.*)\((.*?),(.*?)\)(.*)/)) {
    const patt = /(.*)\((.*?),(.*?)\)(.*)/;
    let insideBrackets = outputName.replace(patt, "$2" + "," + "$3");
    insideBrackets = insideBrackets.replace(/,/g, "|");
    const outputNameFix = outputName.replace(
      patt,
      "$1" + "(" + insideBrackets + ")" + "$4"
    );
    if (outputNameFix.match(/(.*)\((.*?),(.*?)\)(.*)/)) {
      return fixParentheses(outputNameFix);
    } else {
      return outputNameFix;
    }
  } else {
    return outputName;
  }
};

const parseVarPart = (
  varPart: string,
  type?: string | null
): [string | null, string | string[] | null] => {
  const splitType: string = type ?? "";
  let varName: string | null = null;
  let defaultVal: string | string[] | null = null;
  if (!varPart) return [null, null];
  // Check if 'varPart' contains an '=' indicating a variable assignment
  if (varPart.includes("=")) {
    const delimiter = splitType === "condition" ? "==" : "=";
    const index = varPart.indexOf(delimiter);
    let varNameRaw: string | undefined;
    let defaultValRaw: string | undefined;
    if (index !== -1) {
      varNameRaw = varPart.substring(0, index);
      defaultValRaw = varPart.substring(index + delimiter.length); // + delimiter.length to remove the delimiter itself
    }

    // Ensure both variable name and value are defined after splitting
    if (varNameRaw !== undefined && defaultValRaw !== undefined) {
      varName = varNameRaw.trim();
      defaultVal = defaultValRaw.trim();

      // Remove surrounding quotes from defaultVal if present
      if (/^["'].*["']$/.test(defaultVal)) {
        defaultVal = defaultVal.slice(1, -1);
      }
      // Check for handsontable specific format and skip modification
      else if (!defaultVal.startsWith("[[")) {
        // Handle array-like syntax by removing potential surrounding brackets and quotes inside
        if (defaultVal.startsWith("[") && defaultVal.endsWith("]")) {
          defaultVal = defaultVal.slice(1, -1).replace(/["']/g, "").split(",");
        }
      }
      // If the condition above is not met, no further processing is needed for handsontable format
    }
  } // if /=/ not exist then genericCond is defined
  else {
    if (splitType === "condition") {
      varName = varPart.trim();
      defaultVal = null;
    }
  }
  return [varName, defaultVal];
};

/**
 * Resolves what the run action button should present, from both the polled server status and
 * whatever the user has in flight locally.
 *
 * Launching chains two mutations: save, then initiate, where the initiate call is made from an
 * effect that watches the save result. That leaves a render in which neither mutation is
 * pending, and React paints it before the effect runs. Deriving the label straight from the
 * mutation flags therefore flashed the previous attempt's status ("Run Error") for a frame in
 * the middle of a launch. `isLaunchRequested` spans the whole chain and closes that gap.
 *
 * `text` and `style` differ only for a never-submitted run, which offers a green "Run" button.
 */
export const resolveRunActionStatus = (params: {
  /** a save, initiate or terminate mutation is currently pending */
  isMutationPending: boolean;
  /** a launch has been requested and has not settled yet, spanning the save -> initiate gap */
  isLaunchRequested: boolean;
  isInitiateRunDisable: boolean;
  isAnyOldRunAttempSelected: boolean;
  runStatus: RunStatus | undefined;
}): { text: RunStatus | string | undefined; style: RunStatus | undefined } => {
  if (params.isMutationPending || params.isLaunchRequested) {
    return { text: RunStatus.Aborted, style: RunStatus.Aborted };
  }
  if (params.isInitiateRunDisable && !params.isAnyOldRunAttempSelected) {
    return { text: RunStatus.NotSubmitted, style: RunStatus.NotSubmitted };
  }
  if (params.runStatus === RunStatus.NotSubmitted) {
    return { text: "Run", style: RunStatus.NextSuccess };
  }
  return { text: params.runStatus, style: params.runStatus };
};

// Rows written in the pre-rename vocabulary (NextErr, NextSuc, ...) would otherwise fall
// through to the "Connecting" default, so map them to the canonical value first.
const canonicalRunStatus = <T extends string | undefined>(
  value: T
): T | RunStatus =>
  (typeof value === "string" && normalizeRunStatus(value)) || value;

export const getRunStatusText = (
  value: RunStatus | string | undefined
): ReactNode => {
  switch (canonicalRunStatus(value)) {
    case RunStatus.NextError:
      return "Run Error";
    case RunStatus.Error:
      return "Run Error";
    case RunStatus.NextSuccess:
      return "Completed";
    case RunStatus.NextRun:
      return "Running";
    case RunStatus.Init:
      return "Initializing..";
    case RunStatus.Terminated:
      return "Terminated";
    case RunStatus.NotSubmitted:
      return "Waiting";
    case RunStatus.Aborted:
      return "Connecting..";
    case RunStatus.Waiting:
      return "Initializing..";
    case "Run":
      return (
        <>
          <PlayIcon className="mr-1 h-5" />
          Run
        </>
      );
    default:
      return "Connecting..";
  }
};

export const getRunStatusStyle = (value: RunStatus | undefined): string => {
  switch (canonicalRunStatus(value)) {
    case RunStatus.NextError:
      return "h-[38px] whitespace-nowrap bg-red-600 hover:bg-red-500 focus-visible:outline-red-600";
    case RunStatus.Error:
      return "h-[38px] whitespace-nowrap bg-red-600 hover:bg-red-500 focus-visible:outline-red-600";
    case RunStatus.NextSuccess:
      return "h-[38px] whitespace-nowrap bg-green-600 hover:bg-green-500 focus-visible:outline-green-600";
    case RunStatus.NextRun:
      return "h-[38px] whitespace-nowrap bg-[#00a7cc] hover:bg-[#00c0ef] focus-visible:outline-[#00a7cc]";
    case RunStatus.Init:
      return "h-[38px] whitespace-nowrap bg-[#00a7cc] hover:bg-[#00c0ef] focus-visible:outline-[#00a7cc]";
    case RunStatus.Terminated:
      return "h-[38px] whitespace-nowrap bg-red-600 hover:bg-red-500 focus-visible:outline-red-600";
    case RunStatus.NotSubmitted:
      return "h-[38px] whitespace-nowrap bg-[#f39c12] hover:bg-yellow-500 focus-visible:outline-[#f39c12]";
    case RunStatus.Aborted:
      return "h-[38px] whitespace-nowrap bg-[#00a7cc] hover:bg-[#00c0ef] focus-visible:outline-[#00a7cc]";
    case RunStatus.Waiting:
      return "h-[38px] whitespace-nowrap bg-[#00a7cc] hover:bg-[#00c0ef] focus-visible:outline-[#00a7cc]";
    default:
      return "h-[38px] whitespace-nowrap bg-[#00a7cc] hover:bg-[#00c0ef] focus-visible:outline-[#00a7cc]";
  }
};

export const getRunStatusLabel = (value: string): JSX.Element => {
  switch (canonicalRunStatus(value)) {
    case RunStatus.NextError:
      return (
        <Badge
          text={BadgeStatus.ErrorText}
          fillColor={BadgeStatus.ErrorFillColor}
          textColor={BadgeStatus.ErrorTextColor}
        />
      );
    case RunStatus.Error:
      return (
        <Badge
          text={BadgeStatus.ErrorText}
          fillColor={BadgeStatus.ErrorFillColor}
          textColor={BadgeStatus.ErrorTextColor}
        />
      );
    case RunStatus.NextSuccess:
      return (
        <Badge
          text={BadgeStatus.SuccessText}
          fillColor={BadgeStatus.SuccessFillColor}
          textColor={BadgeStatus.SuccessTextColor}
        />
      );
    case RunStatus.NextRun:
      return (
        <Badge
          text={BadgeStatus.RunningText}
          fillColor={BadgeStatus.RunningFillColor}
          textColor={BadgeStatus.RunningTextColor}
        />
      );
    case RunStatus.Init:
      return (
        <Badge
          text={BadgeStatus.InitializingText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
    case RunStatus.Terminated:
      return (
        <Badge
          text={BadgeStatus.TerminatedText}
          fillColor={BadgeStatus.TerminatedFillColor}
          textColor={BadgeStatus.TerminatedTextColor}
        />
      );
    case RunStatus.NotSubmitted:
      return (
        <Badge
          text={BadgeStatus.DefaultText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
    case RunStatus.Aborted:
      return (
        <Badge
          text={BadgeStatus.ConnectingText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
    case RunStatus.Waiting:
      return (
        <Badge
          text={BadgeStatus.InitializingText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
    default:
      return (
        <Badge
          text={BadgeStatus.ConnectingText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
  }
};

export const getAppInstanceStatusLabel = (value: AppStatus): JSX.Element => {
  switch (value) {
    case AppStatus.Creating:
      return (
        <Badge
          text={BadgeStatus.CreatingText}
          fillColor={BadgeStatus.WaitingFillColor}
          textColor={BadgeStatus.WaitingTextColor}
        />
      );
    case AppStatus.Terminated:
      return (
        <Badge
          text={BadgeStatus.TerminatedText}
          fillColor={BadgeStatus.TerminatedFillColor}
          textColor={BadgeStatus.TerminatedTextColor}
        />
      );
    case AppStatus.Running:
      return (
        <Badge
          text={BadgeStatus.RunningText}
          fillColor={BadgeStatus.SuccessFillColor}
          textColor={BadgeStatus.SuccessTextColor}
        />
      );
    case AppStatus.Pending:
      return (
        <Badge
          text={BadgeStatus.PendingText}
          fillColor={BadgeStatus.WaitingFillColor}
          textColor={BadgeStatus.WaitingTextColor}
        />
      );
    default:
      return (
        <Badge
          text={BadgeStatus.DefaultText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
  }
};

export const getAppStatusLabel = (value: ContainerStatus): JSX.Element => {
  switch (value) {
    case ContainerStatus.Creating:
      return (
        <Badge
          text={BadgeStatus.CreatingText}
          fillColor={BadgeStatus.WaitingFillColor}
          textColor={BadgeStatus.WaitingTextColor}
        />
      );
    case ContainerStatus.NotDeployed:
      return (
        <Badge
          text={BadgeStatus.NotDeployedText}
          fillColor={BadgeStatus.WaitingFillColor}
          textColor={BadgeStatus.WaitingTextColor}
        />
      );
    case ContainerStatus.Active:
      return (
        <Badge
          text={BadgeStatus.ActiveText}
          fillColor={BadgeStatus.SuccessFillColor}
          textColor={BadgeStatus.SuccessTextColor}
        />
      );
    default:
      return (
        <Badge
          text={BadgeStatus.DefaultText}
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
  }
};

export const mapInput = (
  input: Record<string, any>,
  prefix?: string
): InputNode => {
  let qualifier = "";

  if (input.type === "input" && !input.file && !input.singleFile) {
    qualifier = "val";
  } else if (input.file) {
    qualifier = "file";
  } else if (input.singleFile) {
    qualifier = "singleFile";
  } else if (input.type === "number") {
    qualifier = "number";
  } else {
    qualifier = input.qualifier ?? "";
  }

  return {
    id: prefix ? `${prefix}.${input.varName}` : input.varName,
    inputId: input.varName || "",
    name: input.varName.split(".").pop() || "",
    inputDetails: {
      ...(input.defaultVal && { defVal: input.defaultVal }),
      ...(input.defVal && { defVal: input.defVal }),
      ...(input.type === "dropdown" && {
        dropDown: input.opt?.join(",") ?? "",
      }),
      ...(input.dropDown && { dropDown: input.dropDown }),
      ...(input.type === "checkbox" && { checkbox: true }),
      ...(input.type === "textbox" && { textbox: true }),
      ...(input.showsett && { showSett: input.showsett?.toString() }),
      ...(input.showSett && { showSett: input.showSett?.toString() }),
      ...(input.desc && { inDescOpt: input.desc?.toString() }),
      ...(input.inDescOpt && { inDescOpt: input.inDescOpt?.toString() }),
      ...(input.label && { inLabelOpt: input.label?.toString() }),
      ...(input.inLabelOpt && { inLabelOpt: input.inLabelOpt?.toString() }),
      ...(input.title && { title: input.title?.toString() }),
      ...(input.tool && { tool: input.tool?.toString() }),
      ...(input.autoform && {
        autoform: input.autoform.flat().map(item => ({
          condition: item[0],
          value: item[1],
        })),
      }),
      ...(input.optional && { optional: input.optional }),
      ...(input.hidden && { hidden: input.hidden }),
      ...(input.valueType && { valueType: input.valueType }),
      ...(input.integrationToken && {
        integrationToken: input.integrationToken,
      }),
    },
    qualifier,
  };
};

/**
 * The pipeline's data entry points: inputs declared `@file`, which is where a
 * collection or dataset gets attached. `@single_file` inputs (a design sheet, a
 * comparison sheet) are excluded, since they describe the data rather than being it.
 */
export const getDataInputs = (inputs: InputNode[]): InputNode[] =>
  inputs.filter(({ qualifier }) => qualifier === "file");

/**
 * True when a pipeline has data entry points and the user filled in none of them.
 *
 * The Run button cannot catch this on its own: it only checks inputs that are not
 * `@optional`, and a pipeline offering alternative entry points (RNA-seq 2.x takes
 * either `reads` or `bam_files`) has to mark every one of them optional, because
 * there is no way to declare "exactly one of these". That leaves the launch
 * ungated, which is how run 12292 started a 51-sample run against no data.
 */
export const hasNoDataAttached = (
  dataInputs: InputNode[],
  getValue: (id: string) => unknown
): boolean =>
  dataInputs.length > 0 &&
  dataInputs.every(({ id }) => isFalsyOrEmpty(getValue(id)));

/**
 * A pipeline's `@required_one_of` groups, resolved against the inputs the Run page renders.
 *
 * Declared as `//* @style @required_one_of:{params.reads, params.bam_files}` and carried on
 * `style[0].requiredOneOf` (#443). Members match `InputNode.id`, which is the `params.*` name.
 *
 * Fails open on purpose: a group naming any input that is not rendered is dropped whole. Dropping
 * only the unknown member would turn a typo into "the other member is required", which blocks the
 * runs the alternative exists for.
 */
export const resolveRequiredInputGroups = (
  style: Record<string, any>[] | undefined,
  inputs: InputNode[]
): InputNode[][] => {
  const declared: unknown = style?.[0]?.requiredOneOf;
  if (!Array.isArray(declared)) return [];
  return declared
    .filter(
      (group): group is string[] => Array.isArray(group) && group.length > 0
    )
    .filter(group =>
      group.every(member => inputs.some(({ id }) => id === member))
    )
    .map(group => inputs.filter(({ id }) => group.includes(id)));
};

/**
 * The `@required_one_of` groups that have no member filled in. `isFalsyOrEmpty` decides, so `0` and
 * `false` count as filled.
 */
export const getUnsatisfiedInputGroups = (
  groups: InputNode[][],
  getValue: (id: string) => unknown
): InputNode[][] =>
  groups.filter(group => group.every(({ id }) => isFalsyOrEmpty(getValue(id))));

/**
 * True when some `@required_one_of` group is made only of data inputs. The Run button then stays
 * disabled until data is attached, so the "no data is attached" warning would contradict the gate.
 */
export const isDataRequiredByGroup = (
  groups: InputNode[][],
  dataInputs: InputNode[]
): boolean =>
  groups.some(
    group =>
      group.length > 0 &&
      group.every(member => dataInputs.some(({ id }) => id === member.id))
  );

export const getPipeInputs = (
  schema: Record<string, any>[]
): { pipeParamsArray: InputNode[]; pipeNonParamsArray: InputNode[] } => {
  const pipeParamsArray = schema
    .filter(input => input.varName.startsWith("params."))
    .map(input => mapInput(input));

  const pipeNonParamsArray = schema
    .filter(input => !input.varName.startsWith("params."))
    .map(input => mapInput(input, "pipe"));

  return { pipeParamsArray, pipeNonParamsArray };
};

export const getProcessParamsInputs = (
  object: Record<string, any>
): InputNode[] => {
  const results: InputNode[] = [];
  for (const [_, value] of Object.entries(object)) {
    if (value?.schema?.some(input => input.varName.startsWith("params."))) {
      const inputs = value?.schema
        ?.filter(input => input.varName.startsWith("params."))
        .map(input => mapInput(input));
      results.push(...filterDuplicatesByProperty(inputs, "name"));
    }
  }
  return results;
};

export const getProcessAndModuleOptions = (
  object: Record<string, any>
): ProcessAndModuleOptions[] => {
  const results: ProcessAndModuleOptions[] = [];
  for (const [key, value] of Object.entries(object)) {
    if (value?.schema?.some(input => !input.varName.startsWith("params."))) {
      const inputs = value?.schema
        ?.filter(input => !input.varName.startsWith("params."))
        .map(input => mapInput(input, key));
      const longVarName = value?.details?.longVarName;
      const processName = value?.details?.processName;
      const processOrgName = value?.details?.processOrgName;
      const onlyModuleName = value?.details?.onlyModuleName;
      const style = value?.style;

      results.push({
        longVarName,
        processName,
        processOrgName,
        onlyModuleName,
        inputs: filterDuplicatesByProperty(inputs, "name"),
        style,
      });
    }
  }
  return results;
};

export const filterDuplicatesByProperty = (
  array: InputNode[],
  property: string
): InputNode[] => {
  return array.reduce((acc: InputNode[], current: InputNode) => {
    // Check if the property value is already in the helper array
    if (!acc.find(item => item[property] === current[property])) {
      acc.push(current);
    }
    return acc;
  }, []);
};

export const filterOutShowSett = (
  filterArray: InputNode[],
  showSettArray: InputNode[]
): InputNode[] => {
  return filterArray.filter(
    ({ id, inputId }) =>
      !showSettArray.some(s =>
        s?.inputDetails?.showSett?.split(",")?.includes(id || inputId)
      )
  );
};

export const convertAndCapitalizeString = (str: string): string => {
  return str
    .replace(/_/g, " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const isEditingAllowed = data => {
  return data.write_group_perm === 1 || data.own === 1 || data.isNewProcess;
};

export const getPermissionsEncoding = (perms: Permissions) => {
  switch (perms) {
    case Permissions.GroupShared:
      return PermissionType.GroupShared;
    case Permissions.SpecificUsersShared:
      return PermissionType.SpecificUsersShared;
    case Permissions.Public:
      return PermissionType.Public;
    case Permissions.UserOwned:
    default:
      return PermissionType.UserOwned;
  }
};

export const convertProcGnumToUnderscorePattern = (input: string): string => {
  // Capture the process gnum after "procGnum-" and the optional module prefix after "p".
  // The prefix keeps every module gnum above the process: procGnum-2p12_4 => 12_4_2.
  const regex = /procGnum-(\d+)(?:p(\d+(?:_\d+)*))?/;
  const match = input.match(regex);

  if (match) {
    // match[1] contains the digits after "procGnum-"
    // match[2] (if present) contains the module prefix after "p"
    if (match[2]) {
      return `${match[2]}_${match[1]}`;
    } else {
      return `${match[1]}`;
    }
  } else {
    // Return the input unchanged if it doesn't match the pattern
    return input;
  }
};

export const convertUnderscoreToProcGnumPattern = (input: string): string => {
  // Capture a run parameter key: "number", or a module prefix of one or more "number_" groups
  // followed by the process gnum (12_2 => procGnum-2p12, 12_4_2 => procGnum-2p12_4).
  const regex = /^(?:(\d+(?:_\d+)*)_)?(\d+)$/;
  const match = input.match(regex);

  if (match) {
    // match[1] (if present) contains the module prefix
    // match[2] contains the process gnum
    if (match[1]) {
      return `procGnum-${match[2]}p${match[1]}`;
    } else {
      return `procGnum-${match[2]}`;
    }
  } else {
    // Return the input unchanged if it doesn't match the pattern
    return input;
  }
};

//parses header_script and create autoFill array.
//eg. [condition:{hostname:ghpcc, var:mm10},statement:{indexPath:"/path"}]
//or generic condition eg. [genCondition:{hostname:null, params.genomeTypePipeline:null}, library:{_species:"human"}]
export const parseAutofill = (
  script: string
): { autoFill: AutoFill[]; systemInputs: string[] } => {
  const autoFill: AutoFill[] = [];
  const systemInputs: string[] = [];
  if (script) {
    //check if autofill comment is exist: //* autofill
    if (script.match(/\/\/\* autofill/i)) {
      const lines = script.split("\n");
      let blockStart: number | null = null; // beginning of autofill block
      let ifBlockStart: number | null = null; // beginning of if block
      let conds = {}; //keep all conditions for if block
      let genConds = {}; //keep all generic conditions for if block
      let states = {}; //keep all statements for if block
      let library = {}; //keep all string filling library for if block
      for (let i = 0; i < lines.length; i++) {
        let varName: string | null = null;
        let defaultVal: string | string[] | null = null;
        let cond: string | undefined | null = null; // each condition
        //first find the line of autofill
        if (lines[i].match(/^\s*\/\/\* autofill\s*$/i)) {
          blockStart = i;
        }
        // parse statements after first line of autofill
        if (blockStart !== null && i > blockStart) {
          // global variables
          if (
            !ifBlockStart &&
            !lines[i].match(/^\s*(?:}\s*else\s+)?if\s*\(/i)
          ) {
            [varName, defaultVal] = parseVarPart(lines[i]);
            if (varName && defaultVal) {
              if (varName.match(/^_.*$/)) {
                library[varName] = defaultVal;
              }
            }
          }
          //find if condition
          if (lines[i].match(/^\s*(?:}\s*else\s+)?if\s*\(/i)) {
            if (ifBlockStart) {
              if (
                conds &&
                states &&
                library &&
                genConds &&
                (!isObjectEmpty(conds) || !isObjectEmpty(genConds)) &&
                (!isObjectEmpty(states) || !isObjectEmpty(library))
              ) {
                autoFill.push({
                  condition: conds,
                  genCondition: genConds,
                  statement: states,
                  library: library,
                });
              }
              //push global variables
            } else if (!ifBlockStart) {
              if (
                conds &&
                states &&
                library &&
                genConds &&
                isObjectEmpty(conds) &&
                isObjectEmpty(genConds) &&
                (!isObjectEmpty(states) || !isObjectEmpty(library))
              ) {
                autoFill.push({
                  condition: conds,
                  genCondition: genConds,
                  statement: states,
                  library: library,
                });
              }
            }
            conds = {};
            genConds = {};
            library = {}; //new library object. Will be used for filling strings.
            states = {}; //new statement object. It will be filled with following statements until next if condition
            ifBlockStart = i;
            // Extract condition inside if() using a more efficient regex
            const ifMatch = lines[i].match(/if\s*\(([^)]+)\)/i);
            cond = ifMatch?.[1];
            if (cond) {
              const condsplit = cond.split("&&");
              condsplit.forEach(el => {
                [varName, defaultVal] = parseVarPart(el, "condition");
                if (varName && defaultVal) {
                  conds[varName] = defaultVal;
                } else if (varName && !defaultVal) {
                  genConds[varName] = defaultVal;
                  if (!("$RUN_ENV_ID" in genConds)) {
                    genConds["$RUN_ENV_ID"] = null;
                  }
                }
              });
            }
            //end of the autofill block: //*or
          } else if (
            lines[i].match(/^\s*\/\/\*\s*$/i) ||
            lines[i].match(/^\s*\/\/\* autofill\s*$/i)
          ) {
            blockStart = null;
            ifBlockStart = null;
            if (
              conds &&
              states &&
              library &&
              genConds &&
              (!isObjectEmpty(conds) || !isObjectEmpty(genConds)) &&
              (!isObjectEmpty(states) || !isObjectEmpty(library))
            ) {
              autoFill.push({
                condition: conds,
                genCondition: genConds,
                statement: states,
                library: library,
              });
            }
            //end of if condition with curly brackets
          } else if (lines[i].trim().match(/^\}$/m)) {
            if (ifBlockStart) {
              ifBlockStart = null;
              if (
                conds &&
                states &&
                library &&
                genConds &&
                (!isObjectEmpty(conds) || !isObjectEmpty(genConds)) &&
                (!isObjectEmpty(states) || !isObjectEmpty(library))
              ) {
                autoFill.push({
                  condition: conds,
                  genCondition: genConds,
                  statement: states,
                  library: library,
                });
              }
            }
            conds = {};
            genConds = {};
            library = {};
            states = {};
            //lines of statements
          } else {
            let varPart;
            if (lines[i].match("//*") && lines[i].split("//*").length > 1) {
              varPart = lines[i].split("//*")[0];
            } else {
              varPart = lines[i];
            }
            if (varPart) {
              [varName, defaultVal] = parseVarPart(varPart);
            }
            if (varName && defaultVal) {
              if (varName.match(/^_.*$/)) {
                library[varName] = defaultVal;
              } else {
                if (varName !== "$DEFAULT_IMAGE") {
                  states[varName] = defaultVal;
                }
                //check if params.VARNAME is defined and return all VARNAMES to fill them as system inputs
                if (ifBlockStart && varName.match(/params\.(.*)/)) {
                  if (!systemInputs.includes(varName)) {
                    systemInputs.push(varName);
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  return { autoFill, systemInputs };
};

export const parseAutofillProcesses = (
  processScripts: Record<string, string>
): Record<string, AutoFill[]> => {
  return Object.entries(processScripts).reduce(
    (acc, [key, value]) => {
      const { autoFill } = parseAutofill(value);
      if (autoFill.length > 0) {
        acc[key] = cleanUpAutofillConditions(autoFill);
      }
      return acc;
    },
    {} as Record<string, AutoFill[]>
  );
};

const addProfileLib = (
  oldLibObj: Record<string, any>,
  profileVariables: string
) => {
  const lines = profileVariables?.split("\n") || [];
  for (let i = 0; i < lines.length; i++) {
    let varName: string | null = null;
    let defaultVal: string | string[] | null = null;
    [varName, defaultVal] = parseVarPart(lines[i]);
    if (varName && defaultVal !== null) {
      oldLibObj[varName] = defaultVal;
    }
  }
};

export const addProfileVar = (
  autoFill: AutoFill[],
  profileVar: Record<string, any>[]
): AutoFill[] => {
  let defaultLib = {};
  if (autoFill) {
    if (profileVar) {
      for (let i = 0; i < profileVar.length; i++) {
        let confirmUpdate = false;
        const proId = profileVar[i].id;
        const proHost = profileVar[i].hostname;
        const proVar = profileVar[i].variable;
        // find conditions that matches $HOSTNAME
        autoFill.forEach(el => {
          if (!confirmUpdate) {
            if (
              el.condition &&
              el.condition !== null &&
              !isObjectEmpty(el.condition)
            ) {
              if (el.condition.$HOSTNAME) {
                if (el.condition.$HOSTNAME === proHost) {
                  // attach $RUN_ENV_ID information instead of grouping based on only $HOSTNAME
                  const tmpRow = JSON.parse(JSON.stringify(el)); // Deep clone using JSON methods
                  tmpRow.condition.$RUN_ENV_ID = proId;
                  addProfileLib(tmpRow.library, proVar);
                  autoFill.push(tmpRow);
                  confirmUpdate = true;
                } else if (el.condition.$HOSTNAME === "default") {
                  defaultLib = JSON.parse(JSON.stringify(el.library)); // Deep clone using JSON methods
                }
              }
            }
          }
        });
        //insert as a new row if not exist in the exising obj
        if (!confirmUpdate) {
          const newCond = {
            condition: {} as Record<string, any>, // Use type assertion,
            genCondition: {},
            statement: {},
            library: defaultLib,
          };
          newCond.condition.$HOSTNAME = proHost;
          newCond.condition.$RUN_ENV_ID = proId;
          addProfileLib(newCond.library, proVar);
          autoFill.push(newCond);
        }
      }
    }

    // clean source elements that has no $RUN_ENV_ID and .$HOSTNAME !== "default"
    for (let a = autoFill.length - 1; a >= 0; a--) {
      if (
        autoFill[a].condition &&
        autoFill[a].condition !== null &&
        !isObjectEmpty(autoFill[a].condition)
      ) {
        if (
          autoFill[a].condition.$HOSTNAME &&
          autoFill[a].condition.$HOSTNAME !== "default" &&
          !("$RUN_ENV_ID" in autoFill[a].condition)
        ) {
          autoFill.splice(a, 1);
        }
      }
    }
  }
  return autoFill;
};

//find each generic condition in other cond&state pairs and get their default values.
const findDefVal = (
  genConditions: Record<string, any>,
  autoFill: AutoFill[]
): Record<string, any>[][] => {
  const genCondDefaultVal: Record<string, any>[][] = [];
  Object.keys(genConditions).forEach(varName => {
    const defValArray: Record<string, any>[] = [];
    autoFill.forEach(elem => {
      // find conditions and library that satisfy varName
      if (
        elem.condition &&
        !isObjectEmpty(elem.condition) &&
        elem.library &&
        !isObjectEmpty(elem.library)
      ) {
        const cond = elem.condition;
        if (cond[varName]) {
          const defaultVal = cond[varName];
          const obj: Record<string, any> = {};
          obj[varName] = defaultVal;
          defValArray.push(obj);
        }
      }
    });
    genCondDefaultVal.push(defValArray);
  });
  return genCondDefaultVal;
};

//Generates combinations from n arrays with m elements
const cartesianProduct = arr => {
  return arr.reduce(
    (a, b) => {
      return a
        .map(x => b.map(y => x.concat(y)))
        .reduce((a, b) => {
          return a.concat(b);
        }, []);
    },
    [[]]
  );
};

//replacing stateValue text by using library object
const fillStateValue = (
  stateValue: string,
  library: Record<string, any>
): string => {
  Object.keys(library).forEach(key => {
    // replace all library keys with values
    const replaceKey = "\\$\\{" + key + "\\}";
    const replaceVal = library[key];
    const re = new RegExp(replaceKey, "g");
    stateValue = stateValue.replace(re, replaceVal);
  });
  return stateValue;
};

// get new statements for each combination of conditions
const getNewStatements = (
  conditions: Record<string, any>[],
  autoFill: AutoFill[],
  genStatement: Record<string, any>
): AutoFill => {
  const newStateCond = {
    condition: {},
    genCondition: {},
    statement: {},
    library: {},
  };
  if (conditions) {
    const mergedLib = {};
    // get Merged library for given conditions
    conditions.forEach(ele => {
      const varName = Object.keys(ele)?.[0];

      const defVal = ele[varName];
      newStateCond.condition[varName] = defVal;
      //find varName = defVal statement in autoFill which has library
      autoFill.forEach(elem => {
        if (
          elem.condition &&
          !isObjectEmpty(elem.condition) &&
          elem.library &&
          !isObjectEmpty(elem.library)
        ) {
          const cond = elem.condition;
          if (cond[varName]) {
            const originDefVal = cond[varName];
            if (originDefVal.toString() === defVal.toString()) {
              const library = elem.library;
              Object.assign(mergedLib, library);
            }
          }
          //find default library that not belong to any condition or generic condition.
        } else if (
          isObjectEmpty(elem.condition) &&
          isObjectEmpty(elem.genCondition) &&
          elem.library &&
          !isObjectEmpty(elem.library)
        ) {
          const deflibrary = elem.library;
          Object.assign(mergedLib, deflibrary);
        }
      });
    });

    // use Merged library to fill genStatement
    const fillWithNewValue = (newStateCond, section, fillName, mergedLib) => {
      Object.keys(section).forEach(key => {
        const stateValue = section[key];
        const newStateValue = fillStateValue(stateValue, mergedLib);
        newStateCond[fillName][key] = newStateValue;
      });
    };
    const sectionAr = [genStatement];
    const fillName = ["statement"];
    for (let i = 0; i < sectionAr.length; i++) {
      fillWithNewValue(newStateCond, sectionAr[i], fillName[i], mergedLib);
    }
  }
  return newStateCond;
};

//reads generic conditions and create condition&statements pairs
//eg. [genCondition:{hostname:null, genomeType:null}, library:{_species:"human"}] to [condition:{hostname:ghpcc, genomeType:human_hg19},statement:{indexPath:"/path"}]
export const decodeGenericCond = (
  autoFill: AutoFill[],
  profileVar: Record<string, any>[]
): AutoFill[] => {
  if (autoFill) {
    autoFill.forEach(el => {
      // find generic conditions
      if (el.genCondition && !isObjectEmpty(el.genCondition)) {
        const oldCondition = el.condition;
        const genConditions = el.genCondition;
        const genStatements = el.statement;
        //find each generic condition in other cond&state pairs and get their default values.
        const genCondDefaultVal = findDefVal(genConditions, autoFill);
        // add $RUN_ENV_ID as suffix to $HOSTNAME before cartesianProduct
        if (profileVar) {
          for (let k = 0; k < profileVar.length; k++) {
            const optionVal = profileVar[k].id;
            const optionHost = profileVar[k].hostname;
            if (optionHost && optionVal) {
              for (let i = 0; i < genCondDefaultVal.length; i++) {
                if (
                  genCondDefaultVal[i] &&
                  genCondDefaultVal[i][0] &&
                  genCondDefaultVal[i][0]["$HOSTNAME"]
                ) {
                  for (let i2 = 0; i2 < genCondDefaultVal[i].length; i2++) {
                    if (
                      genCondDefaultVal[i][i2] &&
                      genCondDefaultVal[i][i2]["$HOSTNAME"] &&
                      genCondDefaultVal[i][i2]["$HOSTNAME"] === optionHost
                    ) {
                      genCondDefaultVal[i][i2]["$HOSTNAME"] =
                        `${optionHost}----${optionVal}`;
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        // remove $RUN_ENV_ID before cartesianProduct
        for (let i = genCondDefaultVal.length - 1; i >= 0; i--) {
          if (
            genCondDefaultVal[i] &&
            genCondDefaultVal[i][0] &&
            genCondDefaultVal[i][0]["$RUN_ENV_ID"]
          ) {
            genCondDefaultVal.splice(i, 1);
          }
        }

        // get combinations array of each conditions
        const combiConditions = cartesianProduct(genCondDefaultVal);

        // // separate $RUN_ENV_ID  after cartesianProduct and add as new array element
        for (let c = 0; c < combiConditions.length; c++) {
          const combiCondArr = combiConditions[c];

          for (let a = 0; a < combiCondArr.length; a++) {
            if (
              combiCondArr[a] &&
              combiCondArr[a]["$HOSTNAME"] &&
              combiCondArr[a]["$HOSTNAME"].match("----")
            ) {
              const splitedArr = combiCondArr[a]["$HOSTNAME"].split("----");
              //combiCondArr[a]["$HOSTNAME"] is  shared objects so we will replace its value after this for loop
              // combiCondArr[a]["$HOSTNAME"] = splitedArr[0]
              combiConditions[c].push({ $RUN_ENV_ID: splitedArr[1] });
            }
          }
          // if condition defined keep this condition after cartesianProduct
          // if (params.genome_build == "human_hg38_gencode_v32_cellranger_v6" && $HOSTNAME){
          //     params.VDJ_reference = "${_shareGen}/${_species}/human/"
          // }
          if (
            oldCondition &&
            typeof oldCondition === "object" &&
            Object.keys(oldCondition).length > 0
          ) {
            combiConditions[c].push(oldCondition);
          }
        }
        //combiCondArr[a]["$HOSTNAME"] is  shared objects so here we replace its value after inserting $RUN_ENV_ID
        for (let c = 0; c < combiConditions.length; c++) {
          const combiCondArr = combiConditions[c];
          for (let a = 0; a < combiCondArr.length; a++) {
            if (
              combiCondArr[a] &&
              combiCondArr[a]["$HOSTNAME"] &&
              combiCondArr[a]["$HOSTNAME"].match("----")
            ) {
              const splitedArr = combiCondArr[a]["$HOSTNAME"].split("----");
              combiCondArr[a]["$HOSTNAME"] = splitedArr[0];
            }
          }
        }
        // get new statements for each combination of conditions
        combiConditions.forEach(cond => {
          autoFill.push(getNewStatements(cond, autoFill, genStatements));
        });
      }
    });
  }
  return autoFill;
};

export const cleanUpAutofillConditions = (autoFill: AutoFill[]): AutoFill[] => {
  return autoFill
    .filter(
      ({ condition, statement }) =>
        !isObjectEmpty(condition) && !isObjectEmpty(statement)
    )
    .map(({ condition, statement }) => ({ condition, statement }));
};

export const applyAutoFillConditions = (
  autoFill: AutoFill[],
  formValues: Record<string, any>,
  prevFormValues: Record<string, any>,
  setValue: (name: string, value: any) => void,
  lastDirtyFieldPath: string | null,
  processIdPattern?: string
): boolean => {
  let hasAutoFillApplied = false;

  if (isObjectEmpty(prevFormValues)) {
    return hasAutoFillApplied;
  }

  const defaultHostnameStatements = autoFill
    .filter(
      ({ condition }) =>
        Object.keys(condition).length === 1 &&
        condition?.$HOSTNAME === "default"
    )
    .reduce((acc, obj) => {
      return { ...acc, ...obj.statement };
    }, {});

  if (
    getNestedValue(formValues, "selectedRunEnvironmentUpdated") ||
    getNestedValue(formValues, "updateAutofillAfterSelectedRunEnvironment")
  ) {
    for (const [key, value] of Object.entries(defaultHostnameStatements)) {
      if (key === "$DEFAULT_IMAGE") {
        continue;
      } else {
        let formKey = key;
        // if variable not start with "params." then handle as pipeline options
        if (!key.startsWith("params.")) {
          formKey = `pipe.${formKey}`;
        }
        if (key === "$SINGULARITY_IMAGE")
          formKey = "singularityConfig.imagePath";
        if (key === "$SINGULARITY_OPTIONS")
          formKey = "singularityConfig.runOptions";
        if (key === "$DOCKER_IMAGE") formKey = "dockerConfig.image";
        if (key === "$DOCKER_OPTIONS") formKey = "dockerConfig.runOptions";
        if (key === "$TIME")
          formKey = processIdPattern
            ? `execEach.${processIdPattern}.time`
            : "execAll.time";
        if (key === "$CPU")
          formKey = processIdPattern
            ? `execEach.${processIdPattern}.cpu`
            : "execAll.cpu";
        if (key === "$MEMORY")
          formKey = processIdPattern
            ? `execEach.${processIdPattern}.memory`
            : "execAll.memory";
        if (key === "$QUEUE")
          formKey = processIdPattern
            ? `execEach.${processIdPattern}.queue`
            : "execAll.queue";
        if (key === "$EXEC_OPTIONS")
          formKey = processIdPattern
            ? `execEach.${processIdPattern}.options`
            : "execAll.options";
        if (
          key === "$TIME" ||
          key === "$CPU" ||
          key === "$MEMORY" ||
          key === "$QUEUE" ||
          key === "$EXEC_OPTIONS"
        ) {
          const executorCheckbox = processIdPattern
            ? "execEachCheck"
            : "execAllCheck";
          // If the user doesn't change run environment or doesn't refresh run environment, skip applying autofill for default hostname condition
          if (
            !getNestedValue(formValues, executorCheckbox) &&
            (lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
              lastDirtyFieldPath === "runEnvironmentId")
          ) {
            hasAutoFillApplied = true;
            setNestedValue(formValues, executorCheckbox, true);
            setValue(executorCheckbox, true);
          }

          if (processIdPattern) {
            const processExecutorCheckbox = `execEach.${processIdPattern}.check`;
            // If the user doesn't change run environment or doesn't refresh run environment, skip applying autofill for default hostname condition
            if (
              !getNestedValue(formValues, processExecutorCheckbox) &&
              (lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
                lastDirtyFieldPath === "runEnvironmentId")
            ) {
              hasAutoFillApplied = true;
              setNestedValue(formValues, processExecutorCheckbox, true);
              setValue(processExecutorCheckbox, true);
            }
          }
        }
        // Check if the formKey exists in formValues and if its value is different from the one in defaultHostnameStatements
        if (hasNestedValue(formValues, formKey)) {
          // If the user doesn't change run environment or doesn't refresh run environment, skip applying autofill for default hostname condition
          if (
            getNestedValue(formValues, formKey) !== value &&
            (lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
              lastDirtyFieldPath === "runEnvironmentId")
          ) {
            hasAutoFillApplied = true;
            setNestedValue(formValues, formKey, value);
            setValue(formKey, value);
          }
        }
      }
    }
    if (getNestedValue(formValues, "selectedRunEnvironmentUpdated")) {
      setValue("selectedRunEnvironmentUpdated", false);
    }
    if (
      getNestedValue(formValues, "updateAutofillAfterSelectedRunEnvironment")
    ) {
      setValue("updateAutofillAfterSelectedRunEnvironment", false);
    }
  }

  autoFill.forEach(({ condition, statement }) => {
    let allConditionsMet = true;
    const formKeys: string[] = [];

    for (const [key, value] of Object.entries(condition)) {
      if (key === "$HOSTNAME" && value === "default") {
        // TODO: Check this condition
        allConditionsMet = false;
        continue;
      }

      let formKey = key;
      // if variable not start with "params." then handle as pipeline options
      if (!key.startsWith("params.")) {
        formKey = `pipe.${formKey}`;
      }
      if (key === "$RUN_ENV_ID") formKey = "runEnvironmentId";
      if (key === "$SINGULARITY_IMAGE") formKey = "singularityConfig.imagePath";
      if (key === "$SINGULARITY_OPTIONS")
        formKey = "singularityConfig.runOptions";
      if (key === "$DOCKER_IMAGE") formKey = "dockerConfig.image";
      if (key === "$DOCKER_OPTIONS") formKey = "dockerConfig.runOptions";
      if (key === "$TIME")
        formKey = processIdPattern
          ? `execEach.${processIdPattern}.time`
          : "execAll.time";
      if (key === "$CPU")
        formKey = processIdPattern
          ? `execEach.${processIdPattern}.cpu`
          : "execAll.cpu";
      if (key === "$MEMORY")
        formKey = processIdPattern
          ? `execEach.${processIdPattern}.memory`
          : "execAll.memory";
      if (key === "$QUEUE")
        formKey = processIdPattern
          ? `execEach.${processIdPattern}.queue`
          : "execAll.queue";
      if (key === "$EXEC_OPTIONS")
        formKey = processIdPattern
          ? `execEach.${processIdPattern}.options`
          : "execAll.options";
      if (key === "$HOSTNAME") formKey = "hostname";
      if (key === "$DEFAULT_IMAGE") continue;
      if (formKey === "runEnvironmentId") {
        if (
          getNestedValue(formValues, formKey)?.toString() !== value?.toString()
        ) {
          allConditionsMet = false;
          break;
        }
      } else {
        if (getNestedValue(formValues, formKey) !== value) {
          allConditionsMet = false;
          break;
        }
      }
      // Check if the formKey exists in formValues
      if (hasNestedValue(formValues, formKey)) {
        formKeys.push(formKey);
      }
    }

    if (
      allConditionsMet &&
      !formKeys.some(
        formKey =>
          getNestedValue(formValues, formKey)?.toString() !==
          getNestedValue(prevFormValues, formKey)?.toString()
      )
    ) {
      if (
        !getNestedValue(formValues, "selectedRunEnvironmentUpdated") &&
        !getNestedValue(formValues, "updateAutofillAfterSelectedRunEnvironment")
      ) {
        allConditionsMet = false;
      }
    }

    if (allConditionsMet) {
      for (const [key, value] of Object.entries(statement)) {
        if (key === "$DEFAULT_IMAGE") {
          continue;
        } else {
          let formKey = key;
          // if variable not start with "params." then handle as pipeline options
          if (!key.startsWith("params.")) {
            formKey = `pipe.${formKey}`;
          }
          if (key === "$SINGULARITY_IMAGE")
            formKey = "singularityConfig.imagePath";
          if (key === "$SINGULARITY_OPTIONS")
            formKey = "singularityConfig.runOptions";
          if (key === "$DOCKER_IMAGE") formKey = "dockerConfig.image";
          if (key === "$DOCKER_OPTIONS") formKey = "dockerConfig.runOptions";
          if (key === "$TIME")
            formKey = processIdPattern
              ? `execEach.${processIdPattern}.time`
              : "execAll.time";
          if (key === "$CPU")
            formKey = processIdPattern
              ? `execEach.${processIdPattern}.cpu`
              : "execAll.cpu";
          if (key === "$MEMORY")
            formKey = processIdPattern
              ? `execEach.${processIdPattern}.memory`
              : "execAll.memory";
          if (key === "$QUEUE")
            formKey = processIdPattern
              ? `execEach.${processIdPattern}.queue`
              : "execAll.queue";
          if (key === "$EXEC_OPTIONS")
            formKey = processIdPattern
              ? `execEach.${processIdPattern}.options`
              : "execAll.options";
          if (
            key === "$TIME" ||
            key === "$CPU" ||
            key === "$MEMORY" ||
            key === "$QUEUE" ||
            key === "$EXEC_OPTIONS"
          ) {
            const executorCheckbox = processIdPattern
              ? "execEachCheck"
              : "execAllCheck";
            // If the last dirty path is not part of the formKeys or doesn't relevant the run environment changes, skip applying autofill
            if (
              !getNestedValue(formValues, executorCheckbox) &&
              (formKeys.includes(lastDirtyFieldPath || "") ||
                lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
                lastDirtyFieldPath === "runEnvironmentId")
            ) {
              hasAutoFillApplied = true;
              setNestedValue(formValues, executorCheckbox, true);
              setValue(executorCheckbox, true);
            }

            if (processIdPattern) {
              const processExecutorCheckbox = `execEach.${processIdPattern}.check`;
              // If the last dirty path is not part of the formKeys or doesn't relevant the run environment changes, skip applying autofill
              if (
                !getNestedValue(formValues, processExecutorCheckbox) &&
                (formKeys.includes(lastDirtyFieldPath || "") ||
                  lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
                  lastDirtyFieldPath === "runEnvironmentId")
              ) {
                hasAutoFillApplied = true;
                setNestedValue(formValues, processExecutorCheckbox, true);
                setValue(processExecutorCheckbox, true);
              }
            }
          }
          // Check if the formKey exists in formValues and if its value is different from the one in statement
          if (hasNestedValue(formValues, formKey)) {
            // If the last dirty path is not part of the formKeys or doesn't relevant the run environment changes, skip applying autofill
            if (
              getNestedValue(formValues, formKey) !== value &&
              (formKeys.includes(lastDirtyFieldPath || "") ||
                lastDirtyFieldPath === "selectedRunEnvironmentUpdated" ||
                lastDirtyFieldPath === "runEnvironmentId")
            ) {
              hasAutoFillApplied = true;
              setNestedValue(formValues, formKey, value);
              setValue(formKey, value);
            }
          }
        }
        if (getNestedValue(formValues, "selectedRunEnvironmentUpdated")) {
          setValue("selectedRunEnvironmentUpdated", false);
        }
        if (
          getNestedValue(
            formValues,
            "updateAutofillAfterSelectedRunEnvironment"
          )
        ) {
          setValue("updateAutofillAfterSelectedRunEnvironment", false);
        }
      }
    }
  });

  // Debug on autofill applied
  if (hasAutoFillApplied) {
    console.log("AutoFill Applied");
    console.log({
      autoFill,
      formValues,
      prevFormValues,
      processIdPattern,
    });
  }
  return hasAutoFillApplied;
};

export const applyAutoFillProcessConditions = (
  autoFillProcesses: Record<string, AutoFill[]>,
  formValues: Record<string, any>,
  prevFormValues: Record<string, any>,
  setValue: (name: string, value: any) => void,
  lastDirtyFieldPath: string | null
): boolean => {
  let hasAutoFillApplied = false;
  for (const [processIdPattern, autoFill] of Object.entries(
    autoFillProcesses
  )) {
    hasAutoFillApplied = applyAutoFillConditions(
      autoFill,
      formValues,
      prevFormValues,
      setValue,
      lastDirtyFieldPath,
      processIdPattern
    );
  }

  return hasAutoFillApplied;
};

// Utility function to get a nested property value
export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

// Utility function to check if a nested property exists
const hasNestedValue = (obj: Record<string, any>, path: string): boolean => {
  return path
    .split(".")
    .every(key => obj != null && key in obj && ((obj = obj[key]), true));
};

// Utility function to set a nested property value
const setNestedValue = (
  obj: Record<string, any>,
  path: string,
  value: any
): void => {
  const parts = path.split(".");
  const last = parts.pop();
  const target = parts.reduce(
    (acc, part) => acc[part] || (acc[part] = {}),
    obj
  );
  if (last) target[last] = value;
};

enum GroupProcessInputType {
  Multicol = "multicol",
  Spreadsheet = "spreadsheet",
}

export const groupProcessInputs = (
  inputs: InputNode[],
  style: Record<string, any>[]
): {
  inputGroup: InputNode[];
  isArray?: boolean;
  isSpreadsheet?: boolean;
}[] => {
  const inputRows: InputNode[][] = [];
  const matchedInputs = new Set<string>(); // Track matched input IDs

  const checkGroup = (groupType: GroupProcessInputType, input: InputNode) => {
    style?.[0]?.[groupType]?.forEach(group => {
      if (group.includes(input.inputId)) {
        if (
          !inputRows.some(row =>
            row.some(item => item.inputId === input.inputId)
          )
        ) {
          const groupedInputs: InputNode[] = group
            .map(id => inputs.find(inp => inp?.inputId === id))
            .filter(
              (inp): inp is InputNode =>
                !!inp && !matchedInputs.has(inp.inputId)
            ); // Avoid duplicates and undefined
          if (groupedInputs.length > 0) {
            inputRows.push(groupedInputs);
            groupedInputs.forEach(inp => matchedInputs.add(inp.inputId)); // Mark inputs as matched
          }
        }
      }
    });
  };

  // Iterate over inputs
  inputs.forEach(input => {
    // Check both 'multicol' and 'spreadsheet' groups
    checkGroup(GroupProcessInputType.Multicol, input);
    checkGroup(GroupProcessInputType.Spreadsheet, input);

    // If not found in any group, push it as an individual input row
    if (!matchedInputs.has(input.inputId)) {
      inputRows.push([input]);
      matchedInputs.add(input.inputId);
    }
  });

  // Remove any duplicate inputs in each row
  const uniqueInputRows: InputNode[][] = inputRows.map(row => {
    const uniqueRow: InputNode[] = row.filter(
      (input, index, self) =>
        self.findIndex(t => t.inputId === input.inputId) === index
    );
    return uniqueRow;
  });

  return uniqueInputRows.map(inputGroup => {
    return {
      inputGroup,
      isArray: style?.[0]?.array?.some(group =>
        group.every(id => inputGroup.some(input => input.inputId === id))
      ),
      isSpreadsheet: style?.[0]?.spreadsheet?.some(group =>
        group.every(id => inputGroup.some(input => input.inputId === id))
      ),
    };
  });
};

export const getProcessInputArrayIndices = (
  obj?: Record<string, any>,
  inputKeys?: string[]
): string[] => {
  const processedIndices: string[] = [];
  if (obj && inputKeys) {
    for (const key in obj) {
      if (inputKeys.some(inputKey => key.startsWith(inputKey))) {
        const match = key.match(/_ind(\d+)$/);
        if (match) {
          const index = match[0];
          if (!processedIndices.includes(index)) {
            processedIndices.push(index);
          }
        }
      }
    }
  }
  return processedIndices;
};

export const parseEditorProcesses = (
  processScripts: Record<string, string>
): Record<string, any> => {
  const editorPattern = /@editor:\{(.+?)}\}/;

  return Object.entries(processScripts).reduce(
    (acc, [key, value]) => {
      const editorMatch = value.match(editorPattern);
      if (editorMatch) {
        // Manually construct the editor object by splitting key-value pairs
        const editorContent = editorMatch[0].split("@editor:")[1];
        acc[convertProcGnumToUnderscorePattern(key)] =
          JSON.parse(editorContent);
      }

      return acc;
    },
    {} as Record<string, any>
  );
};

export const parseDropdownOptions = (
  id: string,
  optionString: string,
  mainScriptHeaderConfig: string,
  form: UseFormReturn
): Option[] => {
  const parts = optionString.split(",");
  let options: Option[] = [];
  let shouldInclude = true;

  const arrayInputIndice = id.match(/_ind(\d+)$/);

  for (const part of parts) {
    if (part.includes("=")) {
      const [condition, values] = part.split("=");
      const valueList = values.replace(/[()]/g, "").split("|");

      // Search the condition in the form inputs
      const actualFormValue = form.watch(
        `${id.split(".")[0]}.${condition}${arrayInputIndice?.[0] || ""}`
      );

      // Search the condition in main script header config
      const searchPattern = new RegExp(
        `^\\s*${condition}\\s*=\\s*['"]([^'"]+)['"]`
      );
      const matchedInScriptHeaderConfig = searchPattern.exec(
        mainScriptHeaderConfig
      );

      if (
        (matchedInScriptHeaderConfig &&
          valueList.includes(matchedInScriptHeaderConfig[1])) ||
        valueList.includes(actualFormValue)
      ) {
        options = [];
        shouldInclude = true;
      } else {
        shouldInclude = false;
      }
    } else if (shouldInclude) {
      options.push({ label: part, value: part });
    }
  }

  return options;
};

export const shouldDisplayInput = (
  id: string,
  inputId: string,
  conditions: string[][][],
  mainScriptHeaderConfig: string,
  form: UseFormReturn
): boolean => {
  if (!conditions || conditions.length === 0) {
    return true;
  }

  const arrayInputIndice = id.match(/_ind(\d+)$/);

  // Create the conditionGroups object
  const conditionGroups = conditions.reduce(
    (acc, conditionGroup) => {
      conditionGroup.forEach(conditionSet => {
        const [condId, expectedValue] = conditionSet[0].split("=");
        if (!acc[condId]) {
          acc[condId] = [];
        }
        acc[condId].push({
          expectedValue,
          inputsToHide: conditionSet.slice(1),
        });
      });
      return acc;
    },
    {} as Record<string, { expectedValue: string; inputsToHide: string[] }[]>
  );

  // Transform config to reverse the logic: find inputs to hide for each condition
  Object.keys(conditionGroups).forEach(key => {
    const allInputsToHide = Array.from(
      new Set(conditionGroups[key].flatMap(cond => cond.inputsToHide))
    );

    conditionGroups[key] = conditionGroups[key].map(cond => ({
      expectedValue: cond.expectedValue,
      inputsToHide: allInputsToHide
        .filter(input => !cond.inputsToHide.includes(input))
        .map(input => `${input}${arrayInputIndice?.[0] || ""}`),
    }));
  });

  // Evaluate the conditions
  for (const [condId, conditionSets] of Object.entries(conditionGroups)) {
    // Search the condition in the form inputs
    const actualFormValue = form.watch(
      `${id.split(".")[0]}.${condId}${arrayInputIndice?.[0] || ""}`
    );

    // Search the condition in main script header config
    const searchPattern = new RegExp(`^\\s*${condId}\\s*=\\s*['"]([^'"]+)['"]`);
    const matchedInScriptHeaderConfig = searchPattern.exec(
      mainScriptHeaderConfig
    );

    for (const { expectedValue, inputsToHide } of conditionSets) {
      const conditionMet =
        (matchedInScriptHeaderConfig &&
          new RegExp(`^${expectedValue}$`).test(
            matchedInScriptHeaderConfig[1]
          )) ||
        new RegExp(`^${expectedValue}$`).test(actualFormValue);

      if (conditionMet && inputsToHide.includes(inputId)) {
        return false; // Hide the input if it should be hidden
      }
    }
  }

  return true; // Display the input by default
};

export const parseSingleFileEditors = (script: string): Record<string, any> => {
  const result: Record<string, any> = {};
  const paramRegex =
    /params\.(\w+)\s*=\s*["'].*?@single_file.*?@editor:({.*})/gm;

  let match: RegExpExecArray | null;

  while ((match = paramRegex.exec(script)) !== null) {
    const key = match[1]; // Parameter name (e.g., Metadata)
    const jsonObject = match[2]; // JSON object after @editor:

    try {
      // Parse the JSON object and add it to the result
      result[`params.${key}`] = JSON.parse(jsonObject);
    } catch (e) {
      console.error(`Error parsing JSON for key: params.${key}`, e);
    }
  }

  return result;
};

export const isPipelineExternal = (type: PipelineType | null) => {
  return type === PipelineType.nfCore || type === PipelineType.nextFlow;
};

/** The input path to save after an upload: where the server placed it, when it said (agent-5-5), else the modal's own folder. */
export const runInputPathAfterUpload = (
  runUploadDir: string,
  fileName: string,
  placedPath?: string
): string => placedPath ?? `${runUploadDir}/${fileName}`;

const pad = (value: number): string => value.toString().padStart(2, "0");

const secureRandomBytes = (count: number): Uint8Array =>
  globalThis.crypto.getRandomValues(new Uint8Array(count));

/**
 * Decision D-a (agent-5-5): the spreadsheet editor saves under a fresh name on an agent run
 * environment, because placement there never overwrites. `<stem>-<yyyymmddHHMMss>-<6 base36>.<ext>`,
 * in UTC, so two saves in the same second still differ by 36^6 possibilities.
 */
export const versionedSpreadsheetFileName = (
  fileName: string,
  now: Date = new Date(),
  randomBytes: (count: number) => Uint8Array = secureRandomBytes
): string => {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const extension = dot > 0 ? fileName.slice(dot) : "";
  const stamp =
    `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}` +
    `${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`;
  const suffix = Array.from(randomBytes(6), byte =>
    (byte % 36).toString(36)
  ).join("");
  return `${stem}-${stamp}-${suffix}${extension}`;
};
